import { makeStyles } from '@material-ui/styles';
import { Table } from 'antd';
import { useHistory } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import PropTypes from 'prop-types';
import React, { useMemo } from 'react';
import { dayFormatType, formatDay } from 'common/helpers/day';
import generatePath from '~settings/routing';
import { bookingJourneyType } from 'common/constants/booking';

const sortDirection = { descend: 'DESCENDING', ascend: 'ASCENDING' };

const useActionFieldStyles = makeStyles(() => ({
  wrapper: {
    overflowY: 'scroll',
  },
}));

const BookingTableDetails = ({
  tableData,
  loading,
  otherTableFilter = {},
  setOtherTableFilter,
  bookingType,
}) => {
  const { t } = useTranslation(['landlord', 'cms']);
  const classes = useActionFieldStyles();
  const history = useHistory();

  const orders = useMemo(
    () => (tableData?.edges ?? []).map(item => item?.node),
    [tableData?.edges],
  );

  const totalCount = useMemo(() => tableData?.totalCount ?? 0, [tableData]);

  const studentNameField = (_, record) => {
    let studentName = '-';

    if (record.bookingJourney === bookingJourneyType.SEMI_AUTOMATIC) {
      return record?.student?.fullName ?? studentName;
    }

    const { passportFirstName, passportMiddleName, passportLastName } = record?.student ?? {};

    if (passportFirstName) {
      studentName = passportFirstName;
    }

    if (passportMiddleName) {
      studentName =
        studentName === '-' ? passportMiddleName : studentName.concat(' ', passportMiddleName);
    }
    if (passportLastName) {
      studentName =
        studentName === '-' ? passportLastName : studentName.concat(' ', passportLastName);
    }

    return studentName;
  };

  const columns = useMemo(() => {
    const columnList = [
      {
        title: t('landlord:landlord.long_tail.booking.booking_number.field'),
        dataIndex: 'id',
        key: 'id',
        width: 168,
        className: classes.column,
        render: (text, record) => record?.referenceId ?? '-',
      },
      {
        title: t('landlord:landlord.long_tail.booking.student_name.field'),
        dataIndex: 'studentName',
        key: 'studentName',
        width: 172,
        className: classes.column,
        render: studentNameField,
      },
    ];

    if (bookingType === bookingJourneyType.SEMI_AUTOMATIC) {
      columnList.push({
        title: t('landlord:landlord.long_tail.booking.snapshot_student_name.field'),
        dataIndex: 'passportFullName',
        key: 'passportFullName',
        width: 172,
        className: classes.column,
        render: (text, record) => record?.passportFullName || '-',
      });
    }

    return columnList.concat([
      {
        title: t('landlord:landlord.long_tail.booking.move_in_date.field'),
        dataIndex: 'moveInDate',
        key: 'moveInDate',
        width: 152,
        className: classes.column,
        render: text => (text ? formatDay(dayFormatType.UKFORMAT, text) : '-'),
      },
      {
        title: t('landlord:landlord.long_tail.booking.move_out_date.field'),
        dataIndex: 'moveOutDate',
        key: 'moveOutDate',
        width: 152,
        className: classes.column,
        render: text => (text ? formatDay(dayFormatType.UKFORMAT, text) : '-'),
      },
      {
        title: t('landlord:landlord.long_tail.booking.booking_status.field'),
        dataIndex: 'stage',
        key: 'stage',
        width: 188,
        className: classes.column,
        render: text =>
          t(`landlord:landlord.long_tail.booking.booking_status.${text.toLowerCase()}`),
      },
      {
        title: t('landlord:landlord.long_tail.booking.booking_create_date.field'),
        dataIndex: 'createdAt',
        key: 'create_date',
        width: 160,
        sorter: true,
        className: classes.column,
        render: text => (text ? formatDay(dayFormatType.UKFORMAT, text) : '-'),
      },
    ]);
  }, [otherTableFilter, setOtherTableFilter, t, bookingType]);

  const handleTableChange = (_, __, sorter) => {
    if (sorter?.order && sortDirection[sorter.order] !== otherTableFilter.sortDirection) {
      setOtherTableFilter({
        ...otherTableFilter,
        pageNumber: 1,
        sortDirection: sortDirection[sorter.order],
      });
    }
  };

  const handlePageNumberChange = pageNumber => {
    setOtherTableFilter({
      ...otherTableFilter,
      pageNumber,
    });
  };

  const handlePageSizeChange = (_, pageSize) => {
    setOtherTableFilter({
      ...otherTableFilter,
      pageNumber: 1,
      pageSize,
    });
  };

  const openOrderDetailsPage = record => {
    const url =
      record?.bookingJourney === bookingJourneyType.SEMI_AUTOMATIC
        ? generatePath('booking.detail', { id: record?.referenceId })
        : generatePath('booking.manualDetail', { id: record?.referenceId });
    history.push(url);
  };

  return (
    <div className={classes.wrapper}>
      <Table
        rowKey="id"
        columns={columns}
        dataSource={orders}
        loading={loading}
        onChange={handleTableChange}
        pagination={{
          current: otherTableFilter?.pageNumber,
          pageSize: otherTableFilter?.pageSize,
          showSizeChanger: true,
          hideOnSinglePage: false,
          total: totalCount,
          onChange: handlePageNumberChange,
          onShowSizeChange: handlePageSizeChange,
        }}
        onRow={record => ({
          onClick: () => {
            openOrderDetailsPage(record);
          },
        })}
      />
    </div>
  );
};

BookingTableDetails.propTypes = {
  tableData: PropTypes.object,
  loading: PropTypes.bool,
  isHerculesBooking: PropTypes.bool,
  otherTableFilter: PropTypes.object,
  setOtherTableFilter: PropTypes.func,
  refetchData: PropTypes.func,
};

export default BookingTableDetails;
